import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CONFIG } from '@cpd-app/configuration';
import { GoalFacade } from '@cpd-app/state/goal';
import { UserProfileFacade } from '@cpd-app/state/user-profile';
import { map, timer } from 'rxjs';
import { take } from 'rxjs/operators';
import { GuideDialogData } from '@cpd-app/core-module/models';
import { SCROLL_TO } from '@cpd-app/shared-module/constants/tutorial.const';
import { NewsFacade } from '@cpd-app/state/news';
import { NewsItem, ReactionEnum } from '@cpd-app/generated';
import { MatTooltip } from '@angular/material/tooltip';
import { TrackerFacade } from '@cpd-app/state/tracker';
import {
  BUILD_TARGET,
  CAREER_SCROLLERS_ID,
  SCHOOLS_EL_ID
} from '@cpd-app/shared-module/constants';
import { APP_ROUTES } from '@cpd-app/core-module/constants/routes';
import { VideoDialogComponent } from '@cpd-app/shared-module/components/video-dialog/video-dialog.component';
import { STORAGE_KEY } from '@cpd-app/core-module/constants/storage-key.const';

@Component({
  templateUrl: './tutorial-dialog.component.html',
  styleUrls: ['./tutorial-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TutorialDialogComponent implements OnInit {
  public selectedTab: 'tutorial' | 'news' = 'tutorial';
  public surveyCompletion = 0;
  public selectedNews: NewsItem;

  public userProfile$ = this.userProfileFacade.userProfile$.pipe(
    map((userProfile) => {
      this.surveyCompletion =
        Number(userProfile.isSkillsSurveyComplete) +
        Number(userProfile.isLifestyleSurveyComplete) +
        Number(userProfile.isInterestsSurveyComplete) +
        Number(userProfile.isWorkValuesSurveyComplete);

      return userProfile;
    })
  );
  public isAirForce$ = this.userProfileFacade.isAirForce$;
  public isSpaceForce$ = this.userProfileFacade.isSpaceForce$;
  public isAllSurveyComplete$ = this.userProfileFacade.isAllSurveyComplete$;
  public isCareerGoalSet$ = this.goalFacade.isCareerGoalSet$;
  public goals$ = this.goalFacade.goals$;
  public isAcademicInstitutionProgramGoalSet$ =
    this.goalFacade.isAcademicInstitutionProgramGoalSet$;

  public isEnableToDownload$ = this.goalFacade.isEnableToGetReturnLink$;
  public news$ = this.newsFacade.news$;
  public isDialogOpened$ = this.dialogRef?.afterOpened().pipe(map(() => true));
  public reportShared$ = this.goalFacade.reportShared$;
  public returnLink$ = this.goalFacade.returnLink$;

  public readonly tutorialVideos = [
    CONFIG.STATIC_CONTENT.TUTORIAL_VIDEO.TAKE_SURVEYS,
    CONFIG.STATIC_CONTENT.TUTORIAL_VIDEO.SET_CAREER_GOAL,
    CONFIG.STATIC_CONTENT.TUTORIAL_VIDEO.SET_ACADEMIC_GOAL,
    CONFIG.STATIC_CONTENT.TUTORIAL_VIDEO.SAVE_RETURN_LINK
  ];

  constructor(
    private dialogRef: MatDialogRef<TutorialDialogComponent>,
    private dialog: MatDialog,
    private router: Router,
    private goalFacade: GoalFacade,
    private userProfileFacade: UserProfileFacade,
    private newsFacade: NewsFacade,
    private trackerFacade: TrackerFacade
  ) {}

  public ngOnInit(): void {
    this.newsFacade.getNews();
  }

  public onReactOnNews(news: NewsItem, reaction: ReactionEnum): void {
    if (this.selectedNews) {
      this.selectedNews.reaction = reaction === ReactionEnum.Like ? 1 : -1;
    }

    this.newsFacade.reactOnNews({ newsId: news.id, type: reaction });
  }

  public goToTheSurveys(): void {
    if (this.router.url.includes(APP_ROUTES.CareerPlanning.BuildTarget)) {
      this.dialogRef.close();
      timer(500)
        .pipe(take(1))
        .subscribe(() => {
          document.getElementById(BUILD_TARGET).scrollIntoView({
            behavior: 'smooth'
          });
        });
      return;
    }

    this.router.navigateByUrl(APP_ROUTES.CareerPlanning.BuildTarget);
    sessionStorage.setItem(STORAGE_KEY.SCROLL, SCROLL_TO.BUILD_YOUR_TARGET);
    this.dialogRef.close();
  }

  public goToCareersScroller(): void {
    if (this.router.url.includes(APP_ROUTES.CareerPlanning.BuildTarget)) {
      this.dialogRef.close();
      timer(500)
        .pipe(take(1))
        .subscribe(() => {
          document.getElementById(CAREER_SCROLLERS_ID).scrollIntoView({
            behavior: 'smooth'
          });
        });
      return;
    }

    this.router.navigateByUrl(APP_ROUTES.CareerPlanning.BuildTarget);
    sessionStorage.setItem(STORAGE_KEY.SCROLL, SCROLL_TO.CAREER_SCROLLERS);
    this.dialogRef.close();
  }

  public goToInstitutions(onetCode: string): void {
    if (this.router.url.includes(APP_ROUTES.Occupation)) {
      this.dialogRef.close();
      timer(500)
        .pipe(take(1))
        .subscribe(() => {
          document.getElementById(SCHOOLS_EL_ID).scrollIntoView({
            behavior: 'smooth'
          });
        });
      return;
    }

    this.router.navigate([APP_ROUTES.Occupation, onetCode]);
    sessionStorage.setItem(STORAGE_KEY.SCROLL, SCROLL_TO.INSTITUTION);
    this.dialogRef.close();
  }

  public openGuidDialog(title: string): void {
    const video = this.tutorialVideos.find((item) => item.title === title);

    if (video) {
      this.dialog.open<VideoDialogComponent, GuideDialogData>(
        VideoDialogComponent,
        {
          width: '70%',
          maxWidth: '950px',
          data: {
            title: title,
            videoResource: video.videoUrl,
            videoSubtitleUrl: video.videoSubtitleUrl
          }
        }
      );
    }
  }

  public copy(returnLink: string, tooltip: MatTooltip): void {
    if (returnLink && tooltip) {
      tooltip.show();
      navigator.clipboard.writeText(returnLink);
      this.trackerFacade.reportShared();

      timer(2000)
        .pipe(take(1))
        .subscribe(() => {
          tooltip.hide();
        });
    }
  }
}
